import { z } from 'zod';

export const ASSET_TYPES = [
  'laptop',
  'desktop',
  'phone',
  'tablet',
  'monitor',
  'keyboard',
  'mouse',
  'headset',
  'chair',
  'desk',
  'vehicle',
  'other',
] as const;
export const assetTypeEnum = z.enum(ASSET_TYPES);
export type AssetType = z.infer<typeof assetTypeEnum>;

export const ASSET_STATUSES = ['available', 'assigned', 'maintenance', 'retired'] as const;
export const assetStatusEnum = z.enum(ASSET_STATUSES);
export type AssetStatus = z.infer<typeof assetStatusEnum>;

export const ASSET_CONDITIONS = ['new', 'good', 'fair', 'poor'] as const;
export const assetConditionEnum = z.enum(ASSET_CONDITIONS);
export type AssetCondition = z.infer<typeof assetConditionEnum>;

/**
 * Company IT equipment — laptops, monitors, phones, etc. — tracked through an
 * assign/return/maintenance lifecycle with a full history log. Ported from
 * Office_project's `Asset` model.
 */
export const createAssetSchema = z.object({
  assetTag: z.string().trim().min(1, 'Asset tag is required').max(60),
  name: z.string().trim().min(1, 'Name is required').max(200),
  type: assetTypeEnum.default('other'),
  brand: z.string().trim().max(120).optional().or(z.literal('')),
  model: z.string().trim().max(120).optional().or(z.literal('')),
  serialNumber: z.string().trim().max(120).optional().or(z.literal('')),
  condition: assetConditionEnum.default('good'),
  purchaseDate: z.coerce.date().optional(),
  purchasePrice: z.coerce.number().min(0).default(0),
  warrantyExpiry: z.coerce.date().optional(),
  location: z.string().trim().max(200).optional().or(z.literal('')),
  notes: z.string().trim().max(2000).optional().or(z.literal('')),
});

export type CreateAssetInput = z.infer<typeof createAssetSchema>;

/** Matches the source's own field allow-list — `assetTag` is immutable, and `status`/`assignedTo` are owned by the assign/return/maintenance actions, not a normal edit. */
export const updateAssetSchema = createAssetSchema.omit({ assetTag: true }).partial();

export type UpdateAssetInput = z.infer<typeof updateAssetSchema>;

export const assignAssetSchema = z.object({
  userId: z.string().min(1, 'userId is required'),
  note: z.string().trim().max(1000).optional().or(z.literal('')),
});

export type AssignAssetInput = z.infer<typeof assignAssetSchema>;

export const returnAssetSchema = z.object({
  note: z.string().trim().max(1000).optional().or(z.literal('')),
  condition: assetConditionEnum.optional(),
});

export type ReturnAssetInput = z.infer<typeof returnAssetSchema>;

export const maintenanceAssetSchema = z.object({
  note: z.string().trim().max(1000).optional().or(z.literal('')),
});

export type MaintenanceAssetInput = z.infer<typeof maintenanceAssetSchema>;

export const assetListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: assetStatusEnum.optional(),
  type: assetTypeEnum.optional(),
  assignedTo: z.string().optional(),
  search: z.string().trim().max(200).optional(),
});

export type AssetListQuery = z.infer<typeof assetListQuerySchema>;
